import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { withDevtools } from '@ngrx-toolkit/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';

import { ShoppingFlowType } from '@ph-shopping/model/shopping-flow-type.enum';
import { ShoppingStepsUrls } from '@ph-shopping/model/shopping-step-urls.model';
import { ShoppingStep, ShoppingStepDefinition, StepperNavigationOptions } from '@ph-shopping/modules/shopping-stepper';

import {
  blankAllStepUrls,
  decorateSteps,
  flattenedSteps,
  invalidateStepsAfter,
  toggleAllStepsValidity,
  updateShoppingProgress,
  updateStep,
} from './shopping-stepper.utils';
import { withConditionalStorageSync } from '@ph-core/utils/with-conditional-storage-sync';

const storeKey = 'ShoppingStepperState';

export interface ShoppingStepperState {
  currentStepIdx: number;
  currentStep: ShoppingStep | null;
  steps: ShoppingStep[];
  data: Record<string, unknown>;
}

const InitialState: ShoppingStepperState = {
  currentStepIdx: 0,
  steps: [],
  currentStep: null,
  data: {},
};

export type ShoppingStepperStoreType = InstanceType<typeof ShoppingStepperStore>;

export const ShoppingStepperStore = signalStore(
  { providedIn: 'root' },
  withDevtools(storeKey),
  withConditionalStorageSync({ key: storeKey }),
  withState<ShoppingStepperState>(InitialState),
  withMethods((store, router = inject(Router)) => ({
    initializeSteps(stepDefinitions: ShoppingStepDefinition[]): void {
      patchState(store, (state) => {
        if (!state.steps.length) {
          const steps = decorateSteps(stepDefinitions);

          return {
            currentStepIdx: 0,
            currentStep: steps?.length ? steps[0] : undefined,
            steps: steps?.length ? [{ ...steps[0], visited: true }, ...steps.slice(1)] : [],
          };
        } else {
          return state;
        }
      });
    },

    switchShoppingPath(name: ShoppingFlowType): void {
      patchState(store, (state) => ({
        steps: updateStep(state.steps, state.currentStep!, { currentPath: name }),
      }));
    },

    nextStep(): void {
      const flat = flattenedSteps(store.steps());
      const nextIdx = store.currentStepIdx() + 1;
      const nextStep = flat[nextIdx];
      if (nextStep) {
        patchState(store, { currentStepIdx: nextIdx, currentStep: nextStep });
        void router.navigate(['/home/shopping', nextStep.url]);
      }
    },

    prevStep(): void {
      const flat = flattenedSteps(store.steps());
      const prevIdx = store.currentStepIdx() - 1;
      const prevStep = flat[prevIdx];
      if (prevIdx >= 0 && prevStep) {
        patchState(store, { currentStepIdx: prevIdx, currentStep: prevStep });
        void router.navigate(['/home/shopping', prevStep.url]);
      }
    },

    switchToStep(index: number, options?: StepperNavigationOptions): void {
      patchState(store, (state) => ({
        currentStepIdx: index,
        currentStep: flattenedSteps(state.steps)[index],
        steps: options?.updateProgress
          ? updateShoppingProgress(state.steps, flattenedSteps(state.steps), index)
          : state.steps,
      }));
      const step = store.currentStep?.();
      if (step) {
        void router.navigate(['/home/shopping', step.url]);
      }
    },

    switchToStepByUrl(url: string, options?: StepperNavigationOptions): void {
      patchState(store, (state) => {
        const index = flattenedSteps(state.steps).findIndex((elem) => (elem.url as string) === url);
        if (index !== undefined) {
          return {
            currentStepIdx: index,
            currentStep: flattenedSteps(state.steps)[index],
            steps: options?.updateProgress
              ? updateShoppingProgress(state.steps, flattenedSteps(state.steps), index)
              : state.steps,
          };
        } else {
          return state;
        }
      });
      const step = store.currentStep?.();
      if (step) {
        void router.navigate(['/home/shopping', step.url]);
      }
    },

    updateShoppingStepsData(data: Record<string, unknown>): void {
      patchState(store, (state) => ({ data: { ...state.data, ...data } }));
    },

    invalidateStepsAfterDealInformation(): void {
      patchState(store, (state) => {
        const flat = flattenedSteps(state.steps);
        const dealIdx = flat.findIndex((s) => (s.url as string) === ShoppingStepsUrls.DealInformation);
        if (dealIdx < 0) {
          return state;
        }

        return {
          steps: invalidateStepsAfter(state.steps, flat, dealIdx),
        };
      });
    },

    resetShoppingProgressToFirstStep(): void {
      patchState(store, (state) => ({ steps: toggleAllStepsValidity(state.steps, false) }));
    },

    updateCurrentStep(data: Partial<ShoppingStep>): void {
      patchState(store, (state) => ({
        steps: updateStep(
          data.valid === false
            ? invalidateStepsAfter(state.steps, flattenedSteps(state.steps), state.currentStepIdx)
            : state.steps,
          state.currentStep!,
          { ...data }
        ),
      }));
    },

    updateStepByUrl(url: string, data: Partial<ShoppingStep>): void {
      patchState(store, (state) => {
        const index = flattenedSteps(state.steps).findIndex((elem) => (elem.url as string) === url);
        if (index !== undefined) {
          return {
            steps: updateStep(state.steps, flattenedSteps(state.steps)[index], data),
          };
        } else {
          return state;
        }
      });
    },

    markAllStepsAsValidAndVisited(): void {
      patchState(store, (state) => ({ steps: toggleAllStepsValidity(state.steps, true) }));
    },

    markAllStepsWithNoDestination(): void {
      patchState(store, (state) => ({ steps: blankAllStepUrls(state.steps) }));
    },

    resetShoppingProgress(): void {
      patchState(store, () => ({ ...InitialState }));
    },
  }))
);
